/**
 * THE STRIPE REDIRECT GUARD, shared by App (Connect links) and Book (hosted
 * Checkout). Each caller passes ITS OWN exact host set: App leaves only for
 * `connect.stripe.com`, Book only for `checkout.stripe.com`. A host belongs in
 * a set only with evidence that Stripe mints that purpose's links on it, and
 * never as a wildcard (docs/integrations/stripe-connect.md, "Redirect
 * validation"; docs/security/checkout-identity-claim-design.md §2.6).
 */

/**
 * Printable ASCII only, no space. The URL parser silently strips leading and
 * trailing controls and spaces, removes tabs and newlines from the middle and
 * IDNA-maps Unicode hosts (a Cyrillic U+043E "o" becomes punycode, a fullwidth
 * dot becomes "."), so a string can parse to something other than what it
 * shows. A Stripe link is plain ASCII; anything else is refused before parsing.
 */
const PRINTABLE_ASCII = /^[\x21-\x7e]+$/;

/**
 * The validated navigation target for `raw`, or `null` if it is not a link on
 * one of `hosts` that a page may follow. Requires all of: an absolute URL,
 * `https:`, an exact host from `hosts`, no credentials, the default port, and
 * a path (a Stripe link always carries one; the bare host is never a link).
 *
 * The path, query and fragment are otherwise not constrained: Stripe documents
 * its links as opaque URLs. The fragment is kept (`url.href`), because a
 * hosted Checkout URL carries state there.
 */
export function stripeRedirectTarget(raw: unknown, hosts: ReadonlySet<string>): string | null {
  if (typeof raw !== "string" || !PRINTABLE_ASCII.test(raw)) return null;
  let url: URL;
  try {
    // No base: a relative or protocol-relative string throws, and is refused.
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.protocol !== "https:") return null;
  if (!hosts.has(url.hostname)) return null;
  if (url.username !== "" || url.password !== "") return null;
  if (url.port !== "") return null;
  if (url.pathname.length <= 1) return null;
  return url.href;
}
